import type { LangMap } from "./index";

/** Valutazione intelligente: tipi di domanda, test adattivo, correzione automatica. */
const it: Record<string, unknown> = {
  true: "Vero",
  false: "Falso",
  shortPh: "Risposta breve",
  numberPh: "Numero",
  blankPh: "Spazio {{n}}",
  orderPh: "Posizione {{n}}",
  matchPh: "Abbina a…",
  next: "Avanti",
  loading: "Preparo la domanda...",
  progress: "Domanda {{n}} di {{total}}",
  startAdaptive: "Inizia il test adattivo",
  adaptive: "Test adattivo",
  adaptiveHint: "Le domande si adattano: più facili se sbagli, più difficili se vai bene.",
  autoScore: "Voto automatico: {{score}}/{{max}}",
  partial: "{{percent}}% corretto sulle domande a correzione automatica",
  teacherPending: "Le domande aperte le valuta il professore.",
  manual: "Da valutare",
  yours: "La tua risposta",
  expected: "Risposta corretta",
  review: "Da ripassare",
  retry: "Rifai il test",
  coach: "Chiedi a Lumen cosa ripassare",
  coaching: "Lumen sta guardando le tue risposte...",
  autoBadge: "Auto {{percent}}%",
  gradedAuto: "Corretto in automatico",
  gradedTeacher: "Valutato dal professore",
  diff: { easy: "Facile", medium: "Media", hard: "Difficile" },
  type: {
    open: "Aperta",
    multiple: "Scelta multipla",
    truefalse: "Vero/Falso",
    short: "Risposta breve",
    numeric: "Numerica",
    fill_blank: "Completamento",
    matching: "Abbinamento",
    order: "Ordinamento",
  },
};

const en: Record<string, unknown> = {
  true: "True",
  false: "False",
  shortPh: "Short answer",
  numberPh: "Number",
  blankPh: "Blank {{n}}",
  orderPh: "Position {{n}}",
  matchPh: "Match with…",
  next: "Next",
  loading: "Preparing the question...",
  progress: "Question {{n}} of {{total}}",
  startAdaptive: "Start the adaptive test",
  adaptive: "Adaptive test",
  adaptiveHint: "Questions adapt: easier when you miss one, harder when you're doing well.",
  autoScore: "Auto score: {{score}}/{{max}}",
  partial: "{{percent}}% correct on the auto-graded questions",
  teacherPending: "Open questions are graded by your teacher.",
  manual: "To be graded",
  yours: "Your answer",
  expected: "Correct answer",
  review: "To review",
  retry: "Retake the test",
  coach: "Ask Lumen what to review",
  coaching: "Lumen is reading your answers...",
  autoBadge: "Auto {{percent}}%",
  gradedAuto: "Auto-graded",
  gradedTeacher: "Graded by teacher",
  diff: { easy: "Easy", medium: "Medium", hard: "Hard" },
  type: {
    open: "Open",
    multiple: "Multiple choice",
    truefalse: "True/False",
    short: "Short answer",
    numeric: "Numeric",
    fill_blank: "Fill in the blanks",
    matching: "Matching",
    order: "Ordering",
  },
};

const es: Record<string, unknown> = {
  true: "Verdadero",
  false: "Falso",
  shortPh: "Respuesta breve",
  numberPh: "Número",
  blankPh: "Hueco {{n}}",
  orderPh: "Posición {{n}}",
  matchPh: "Relaciona con…",
  next: "Siguiente",
  loading: "Preparando la pregunta...",
  progress: "Pregunta {{n}} de {{total}}",
  startAdaptive: "Empezar el test adaptativo",
  adaptive: "Test adaptativo",
  adaptiveHint: "Las preguntas se adaptan: más fáciles si fallas, más difíciles si aciertas.",
  autoScore: "Nota automática: {{score}}/{{max}}",
  partial: "{{percent}}% correcto en las preguntas de corrección automática",
  teacherPending: "Las preguntas abiertas las corrige tu profesor.",
  manual: "Por corregir",
  yours: "Tu respuesta",
  expected: "Respuesta correcta",
  review: "Para repasar",
  retry: "Repetir el test",
  coach: "Pregunta a Lumen qué repasar",
  coaching: "Lumen está revisando tus respuestas...",
  autoBadge: "Auto {{percent}}%",
  gradedAuto: "Corregido automáticamente",
  gradedTeacher: "Corregido por el profesor",
  diff: { easy: "Fácil", medium: "Media", hard: "Difícil" },
  type: {
    open: "Abierta",
    multiple: "Opción múltiple",
    truefalse: "Verdadero/Falso",
    short: "Respuesta breve",
    numeric: "Numérica",
    fill_blank: "Completar huecos",
    matching: "Relacionar",
    order: "Ordenar",
  },
};

const fr: Record<string, unknown> = {
  true: "Vrai",
  false: "Faux",
  shortPh: "Réponse courte",
  numberPh: "Nombre",
  blankPh: "Trou {{n}}",
  orderPh: "Position {{n}}",
  matchPh: "Associer à…",
  next: "Suivant",
  loading: "Préparation de la question...",
  progress: "Question {{n}} sur {{total}}",
  startAdaptive: "Commencer le test adaptatif",
  adaptive: "Test adaptatif",
  adaptiveHint: "Les questions s'adaptent : plus faciles en cas d'erreur, plus difficiles si tu réussis.",
  autoScore: "Note automatique : {{score}}/{{max}}",
  partial: "{{percent}}% de réussite sur les questions auto-corrigées",
  teacherPending: "Les questions ouvertes sont corrigées par ton professeur.",
  manual: "À corriger",
  yours: "Ta réponse",
  expected: "Bonne réponse",
  review: "À réviser",
  retry: "Refaire le test",
  coach: "Demande à Lumen quoi réviser",
  coaching: "Lumen relit tes réponses...",
  autoBadge: "Auto {{percent}}%",
  gradedAuto: "Corrigé automatiquement",
  gradedTeacher: "Corrigé par le professeur",
  diff: { easy: "Facile", medium: "Moyenne", hard: "Difficile" },
  type: {
    open: "Ouverte",
    multiple: "Choix multiple",
    truefalse: "Vrai/Faux",
    short: "Réponse courte",
    numeric: "Numérique",
    fill_blank: "Texte à trous",
    matching: "Association",
    order: "Remise en ordre",
  },
};

const de: Record<string, unknown> = {
  true: "Wahr",
  false: "Falsch",
  shortPh: "Kurze Antwort",
  numberPh: "Zahl",
  blankPh: "Lücke {{n}}",
  orderPh: "Position {{n}}",
  matchPh: "Zuordnen zu…",
  next: "Weiter",
  loading: "Frage wird vorbereitet...",
  progress: "Frage {{n}} von {{total}}",
  startAdaptive: "Adaptiven Test starten",
  adaptive: "Adaptiver Test",
  adaptiveHint: "Die Fragen passen sich an: leichter bei Fehlern, schwerer bei Erfolg.",
  autoScore: "Automatische Note: {{score}}/{{max}}",
  partial: "{{percent}}% richtig bei den automatisch bewerteten Fragen",
  teacherPending: "Offene Fragen bewertet deine Lehrkraft.",
  manual: "Noch zu bewerten",
  yours: "Deine Antwort",
  expected: "Richtige Antwort",
  review: "Zu wiederholen",
  retry: "Test wiederholen",
  coach: "Frag Lumen, was du wiederholen sollst",
  coaching: "Lumen liest deine Antworten...",
  autoBadge: "Auto {{percent}}%",
  gradedAuto: "Automatisch bewertet",
  gradedTeacher: "Von der Lehrkraft bewertet",
  diff: { easy: "Leicht", medium: "Mittel", hard: "Schwer" },
  type: {
    open: "Offen",
    multiple: "Multiple Choice",
    truefalse: "Wahr/Falsch",
    short: "Kurze Antwort",
    numeric: "Numerisch",
    fill_blank: "Lückentext",
    matching: "Zuordnung",
    order: "Reihenfolge",
  },
};

const pt: Record<string, unknown> = {
  true: "Verdadeiro",
  false: "Falso",
  shortPh: "Resposta curta",
  numberPh: "Número",
  blankPh: "Espaço {{n}}",
  orderPh: "Posição {{n}}",
  matchPh: "Associar a…",
  next: "Seguinte",
  loading: "A preparar a pergunta...",
  progress: "Pergunta {{n}} de {{total}}",
  startAdaptive: "Começar o teste adaptativo",
  adaptive: "Teste adaptativo",
  adaptiveHint: "As perguntas adaptam-se: mais fáceis se erras, mais difíceis se acertas.",
  autoScore: "Nota automática: {{score}}/{{max}}",
  partial: "{{percent}}% correto nas perguntas de correção automática",
  teacherPending: "As perguntas abertas são avaliadas pelo professor.",
  manual: "Por avaliar",
  yours: "A tua resposta",
  expected: "Resposta correta",
  review: "Para rever",
  retry: "Repetir o teste",
  coach: "Pergunta ao Lumen o que rever",
  coaching: "O Lumen está a ler as tuas respostas...",
  autoBadge: "Auto {{percent}}%",
  gradedAuto: "Corrigido automaticamente",
  gradedTeacher: "Avaliado pelo professor",
  diff: { easy: "Fácil", medium: "Média", hard: "Difícil" },
  type: {
    open: "Aberta",
    multiple: "Escolha múltipla",
    truefalse: "Verdadeiro/Falso",
    short: "Resposta curta",
    numeric: "Numérica",
    fill_blank: "Preenchimento",
    matching: "Correspondência",
    order: "Ordenação",
  },
};

export const assess: LangMap = { it, en, es, fr, de, pt };
